import { createAttractionTranslationsConfig, type AttractionTranslation } from '@/config/objects/attraction-translations';
import { GenerateTranslationVoiceoversDialog, GenerateTranslationVoiceoversIcon } from '@/components/attractions/translations';
import {
  RegenerateAttractionTranslationAction,
  RegenerateAttractionTranslationIcon,
} from '@/pages/attractions/translations/actions';
import { CrudDetailView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

/**
 * Attraction translation detail page
 */
export function AttractionTranslationDetailPage() {
  const { attractionId, translationId } = useParams<{ attractionId: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [voiceoversDialogOpen, setVoiceoversDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [translation, setTranslation] = useState<AttractionTranslation | null>(null);

  const translationsConfig = useMemo(
    () => (attractionId ? createAttractionTranslationsConfig(attractionId) : null),
    [attractionId],
  );

  // Map the loaded translation's sync flags to human-readable field labels and
  // decide whether offering a voiceover regen makes sense (short/long
  // description are the voiceover-backed fields). Only `=== true` counts as
  // outdated; a null flag means a legacy row with no recorded source hash.
  const outdatedFields = useMemo(() => {
    if (!translation) return [] as string[];
    const fields: string[] = [];
    if (translation.isNameOutdated === true) fields.push('Name');
    if (translation.isDescriptionOutdated === true) fields.push('Short description');
    if (translation.isLongDescriptionOutdated === true) fields.push('Long description');
    return fields;
  }, [translation]);
  const canRegenerateVoiceovers = Boolean(
    translation?.isDescriptionOutdated === true ||
      translation?.isLongDescriptionOutdated === true,
  );

  /**
   * Delete translation mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attractions/${attractionId}/translations/${id}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Translation'] });
        navigate(`/attractions/${attractionId}`);
      } else {
        throw new Error(response.error || 'Failed to delete translation');
      }
    },
  });

  if (!attractionId || !translationId || !translationsConfig) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
          <p className="text-muted-foreground">The requested translation could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form
   */
  const handleEdit = (translation: AttractionTranslation) => {
    navigate(`/attractions/${attractionId}/translations/${translation.id}/edit`);
  };

  /**
   * Handle translation deletion
   */
  const handleDelete = async (translation: AttractionTranslation) => {
    return toast.promise(deleteMutation.mutateAsync(translation.id), {
      loading: 'Deleting translation...',
      success: 'Translation deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete translation',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: translation?.attraction?.name || 'Attraction', href: `/attractions/${attractionId}` },
    { label: 'Translations', href: `/attractions/${attractionId}` },
    { label: translation?.language?.name || 'Translation', current: true },
  ];

  return (
    <PageContainer>
      <CrudDetailView
        config={translationsConfig}
        itemId={translationId}
        breadcrumbs={breadcrumbs}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onData={(translationData: AttractionTranslation) => setTranslation(translationData)}
        customActions={[
          {
            key: 'regenerate-translation',
            label: outdatedFields.length > 0 ? 'Sync with source' : 'Regenerate translation',
            icon: RegenerateAttractionTranslationIcon,
            variant: 'secondary',
            onClick: () => setRegenerateDialogOpen(true),
          },
          {
            key: 'generate-voiceovers',
            label: 'Generate Voiceovers',
            icon: GenerateTranslationVoiceoversIcon,
            variant: 'secondary',
            onClick: () => setVoiceoversDialogOpen(true),
          },
        ]}
      />

      {attractionId && translationId && (
        <RegenerateAttractionTranslationAction
          attractionId={attractionId}
          translationId={translationId}
          open={regenerateDialogOpen}
          onOpenChange={setRegenerateDialogOpen}
          outdatedFields={outdatedFields}
          canRegenerateVoiceovers={canRegenerateVoiceovers}
        />
      )}

      {attractionId && translationId && (
        <GenerateTranslationVoiceoversDialog
          attractionId={attractionId}
          translationId={translationId}
          open={voiceoversDialogOpen}
          onOpenChange={setVoiceoversDialogOpen}
        />
      )}
    </PageContainer>
  );
}
